import React, { useState, useEffect, useRef } from 'react';
import { Modal } from '@/components/modal';
import { X, Check, RotateCcw, Ban } from 'lucide-react';
import { HarborAssociationRequest } from '@/services/HarborAssociationService';
import { formatDisplayDateTime } from '@/utils/Date';

interface HarborClubRequestDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: HarborAssociationRequest;
  onApprove: (id: string, message?: string) => void;
  onReject: (id: string, message?: string) => void;
  onRevoke?: (id: string, reason?: string) => void;
  isApproving: boolean;
  isRejecting: boolean;
  isRevoking?: boolean;
}

const HarborClubRequestDetailsModal: React.FC<
  HarborClubRequestDetailsModalProps
> = ({
  isOpen,
  onClose,
  request,
  onApprove,
  onReject,
  onRevoke,
  isApproving,
  isRejecting,
  isRevoking = false,
}) => {
  const [responseMessage, setResponseMessage] = useState('');
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [responseType, setResponseType] = useState<
    'approve' | 'reject' | 'revoke'
  >('approve');

  const handleApprove = () => {
    if (showResponseForm) {
      onApprove(request.id, responseMessage);
      setShowResponseForm(false);
      setResponseMessage('');
    } else {
      setResponseType('approve');
      setShowResponseForm(true);
    }
  };

  const handleReject = () => {
    if (showResponseForm) {
      onReject(request.id, responseMessage);
      setShowResponseForm(false);
      setResponseMessage('');
    } else {
      setResponseType('reject');
      setShowResponseForm(true);
    }
  };

  const handleRevoke = () => {
    if (showResponseForm) {
      onRevoke?.(request.id, responseMessage);
      setShowResponseForm(false);
      setResponseMessage('');
    } else {
      setResponseType('revoke');
      setShowResponseForm(true);
    }
  };



  const cancelResponse = () => {
    setShowResponseForm(false);
    setResponseMessage('');
  };

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      const timer = setTimeout(() => {
        modalRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className="!flex items-center justify-center"
      disableEnforceFocus={false}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        autoFocus
        className="bg-gray-100 p-6 w-full max-w-md mx-auto max-h-[90vh] overflow-y-auto rounded-lg space-y-6"
        aria-labelledby="club-request-title"
      >
        <h1
          id="club-request-title"
          className="text-lg font-medium text-gray-900 mb-4"
        >
          Club Request Details
        </h1>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
          <div className="flex items-center mb-4 sm:mb-0">
            {request.club.logo && (
              <img
                src={request.club.logo}
                alt={`${request.club.name} logo`}
                className="w-16 h-16 rounded-full mr-4 object-cover border-2 border-gray-200"
              />
            )}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {request.club.name}
              </h2>
              {request.club.abbreviation && (
                <p className="text-sm text-gray-500">
                  {request.club.abbreviation}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end">
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold 
              ${
                request.status === 'pending'
                  ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                  : request.status === 'approved'
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                    : request.status === 'rejected'
                      ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                      : request.status === 'revoked'
                        ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
                        : request.status === 'approved_by_admin'
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                          : request.status === 'club_approval_pending'
                            ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                            : 'bg-gray-100 text-gray-800'
              }`}
            >
              {(() => {
                switch (request.status) {
                  case 'pending':
                    return 'Pending';
                  case 'approved':
                    return 'Approved';
                  case 'rejected':
                    return 'Rejected';
                  case 'revoked':
                    return 'Revoked';
                  case 'approved_by_admin':
                    return 'Approved by Admin';
                  case 'club_approval_pending':
                    return 'Club Approval Pending';
                  default:
                    return 'Unknown';
                }
              })()}
            </span>
            <p className="text-xs text-gray-500 mt-1">
              Requested on{' '}
              {formatDisplayDateTime(request.created_at, {
                includeSeconds: true,
              })}
            </p>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <h3 className="font-medium text-gray-700 mb-2">Request Message</h3>
          <div className="bg-gray-200 p-4 rounded-md">
            {request.request_message ? (
              <p className="text-gray-600">{request.request_message}</p>
            ) : (
              <p className="text-gray-400 italic">No message provided</p>
            )}
          </div>
        </div>

        {request.response_message && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="font-medium text-gray-700 mb-2">Response Message</h4>
            <div className="bg-gray-100 p-4 rounded-md">
              <p className="text-gray-600">{request.response_message}</p>
            </div>
          </div>
        )}

        {showResponseForm && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="font-medium text-gray-700 mb-2">
              {responseType === 'approve'
                ? 'Approval'
                : responseType === 'reject'
                  ? 'Rejection'
                  : 'Revocation'}{' '}
              Message
              {responseType === 'revoke'
                ? ' (Required)'
                : ' (Optional)'}
            </h4>
            <textarea
              className="w-full textarea p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter a message to the club..."
              rows={3}
              value={responseMessage}
              onChange={(e) => setResponseMessage(e.target.value)}
            />
            <div className="flex justify-end mt-3 space-x-2">
              <button
                onClick={cancelResponse}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-200 flex items-center"
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </button>
              <button
                onClick={
                  responseType === 'approve'
                    ? handleApprove
                    : responseType === 'reject'
                      ? handleReject
                      : handleRevoke
                }
                disabled={
                  isApproving || isRejecting || isRevoking
                }
                className={`px-4 py-2 rounded-md text-white ${
                  responseType === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : responseType === 'revoke'
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {isApproving || isRejecting || isRevoking ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {responseType === 'approve'
                      ? 'Approving...'
                      : responseType === 'reject'
                        ? 'Rejecting...'
                        : 'Revoking...'}
                  </span>
                ) : (
                  <span>
                    Confirm{' '}
                    {responseType === 'approve'
                      ? 'Approval'
                      : responseType === 'reject'
                        ? 'Rejection'
                        : 'Revocation'}
                  </span>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!showResponseForm && (
          <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4">
            <button
              onClick={onClose}
              className="px-2 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-200 flex items-center"
            >
              Close
            </button>

            {/* For pending status - show approve and cancel buttons */}
            {request.status === 'pending' && (
              <>
                <button
                  onClick={handleReject}
                  disabled={isRejecting}
                  className="px-2 py-2 border border-red-300 rounded-md text-red-600 hover:bg-red-50 flex items-center"
                >
                  <Ban className="w-4 h-4 mr-1" />
                  Reject
                </button>
                <button
                  onClick={handleApprove}
                  disabled={isApproving}
                  className="px-2 py-2 bg-green-600 rounded-md text-white hover:bg-green-700 flex items-center"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Approve
                </button>
              </>
            )}

            {/* For club_approval_pending status - no actions allowed from harbor side */}
            {/* This status means waiting for yacht club admin approval */}

            {/* For approved status - show revoke button */}
            {request.status === 'approved' && onRevoke && (
              <button
                onClick={handleRevoke}
                disabled={isRevoking}
                className="px-2 py-2 bg-orange-600 rounded-md text-white hover:bg-orange-700 flex items-center contrast:bg-orange-800 contrast:hover:bg-orange-900"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Revoke
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default HarborClubRequestDetailsModal;
